import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Key } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ChangePasswordDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error(t("change_pwd.mismatch"));
      return;
    }
    if (next.length < 8) {
      toast.error(t("change_pwd.too_short"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success(t("change_pwd.success"));
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className="text-xs uppercase tracking-wider font-bold rounded-none"
          data-testid="change-password-trigger"
          title={t("change_pwd.title")}
        >
          <Key size={16} className="mr-1" /> <span className="hidden lg:inline">{t("change_pwd.short_label")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white rounded-none" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-2xl font-black">{t("change_pwd.title")}</DialogTitle>
          <DialogDescription className="text-zinc-400">{t("change_pwd.subtitle")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold" htmlFor="cp-current">
              {t("change_pwd.current")}
            </Label>
            <Input
              id="cp-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
              className="bg-black border-white/10 rounded-none text-white mt-1"
              data-testid="change-password-current"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold" htmlFor="cp-new">
              {t("change_pwd.new")}
            </Label>
            <Input
              id="cp-new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="bg-black border-white/10 rounded-none text-white mt-1"
              data-testid="change-password-new"
            />
            <div className="text-[10px] text-zinc-500 mt-1">{t("change_pwd.hint")}</div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold" htmlFor="cp-confirm">
              {t("change_pwd.confirm")}
            </Label>
            <Input
              id="cp-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="bg-black border-white/10 rounded-none text-white mt-1"
              data-testid="change-password-confirm"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-11"
            data-testid="change-password-submit"
          >
            {loading ? t("change_pwd.saving") : t("change_pwd.submit")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
